import { CmpMemberLifecycleStatusEnum } from "../../../graphql/types";

export type ManageMembersSortDirection = "asc" | "desc";

type SortableMember = {
  companyName: string;
  id: string;
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
};

type PublicDirectoryVisibilityMember = {
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
  showInApp: boolean;
};

type SortManageMembersInput<Member extends SortableMember, Column> = {
  column: Column;
  direction: ManageMembersSortDirection;
  getSortValue: (member: Member, column: Column) => number | string;
  members: Member[];
};

const compareText = (firstValue: string, secondValue: string): number => {
  const firstNormalizedValue = firstValue.trim();
  const secondNormalizedValue = secondValue.trim();
  const caseInsensitiveResult = firstNormalizedValue.localeCompare(
    secondNormalizedValue,
    "en-CA",
    { numeric: true, sensitivity: "base" },
  );

  return (
    caseInsensitiveResult ||
    firstNormalizedValue.localeCompare(secondNormalizedValue, "en-CA")
  );
};

export const canShowManageMemberPublicDirectoryLink = (
  member: PublicDirectoryVisibilityMember,
): boolean =>
  member.lifecycleStatus === CmpMemberLifecycleStatusEnum.Active &&
  member.showInApp === true;

/** Sort across all statuses. Ties use stable company-name and member-id values. */
export const sortManageMembers = <Member extends SortableMember, Column>({
  column,
  direction,
  getSortValue,
  members,
}: SortManageMembersInput<Member, Column>): Member[] =>
  [...members].sort((firstMember, secondMember) => {
    const firstValue = getSortValue(firstMember, column);
    const secondValue = getSortValue(secondMember, column);
    const primaryResult =
      typeof firstValue === "number" && typeof secondValue === "number"
        ? firstValue - secondValue
        : compareText(String(firstValue), String(secondValue));
    const directedPrimaryResult =
      direction === "desc" ? -primaryResult : primaryResult;

    if (directedPrimaryResult !== 0) {
      return directedPrimaryResult;
    }

    const companyNameResult = compareText(
      firstMember.companyName,
      secondMember.companyName,
    );

    return companyNameResult || compareText(firstMember.id, secondMember.id);
  });
